import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { axiosInstance, INVERTER, LiveData } from "api";
import { getSelectedPowerPlant, useAppSelector } from "store";

export type InverterModelName = "sofar_g1_50_70kw" | "sofar_g3";

export type InverterResponse = {
  id: string;
  name: string;
  model: InverterModelName;
  slaveId: number;
  capacity: number | null;
  isActive: boolean;
  lastSeenAt: string | null;
  liveData: LiveData | null;
};

export type InverterCommandType =
  | "set_power_state"
  | "set_active_power_limit";

export type InverterCommandParams =
  | { state: "on" | "off" }
  | { percent: number };

export type InverterCommandResponse = {
  id: string;
  command: InverterCommandType;
  params: InverterCommandParams;
  executedAt: string | null;
  succeeded: boolean | null;
  errorMessage: string | null;
  createdAt: string;
  inverterId: string;
};

/**
 * Inverter list/config — name, model, slaveId, capacity. Admin-managed and
 * basically static. ``liveData`` and ``lastSeenAt`` on each row refresh on
 * the Pi cadence, but only the Inverter page renders those; callers that
 * just need to know "are there any inverters?" (sidebar + router gate) get
 * the cached value with no polling.
 *
 * Pass ``refetchInterval`` from the Inverter page so its mounted query
 * refreshes per-inverter live data; react-query shares the cache, so the
 * sidebar piggybacks on those refreshes for free while the page is open.
 */
export const useGetInverters = (refetchInterval: number | false = false) => {
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const meterId = selectedPowerPlant?.meterId;

  return useQuery<InverterResponse[]>({
    queryKey: [INVERTER.INVERTERS(meterId!)],
    queryFn: async () => {
      const { data } = await axiosInstance.get<InverterResponse[]>(
        INVERTER.INVERTERS(meterId!),
      );
      return data;
    },
    enabled: !!meterId,
    refetchInterval,
  });
};

export const useGetInverterCommands = (inverterId: string | undefined) => {
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const meterId = selectedPowerPlant?.meterId;

  return useQuery<InverterCommandResponse[]>({
    queryKey: [INVERTER.COMMANDS(meterId!, inverterId!)],
    queryFn: async () => {
      const { data } = await axiosInstance.get<InverterCommandResponse[]>(
        INVERTER.COMMANDS(meterId!, inverterId!),
      );
      return data;
    },
    enabled: !!meterId && !!inverterId,
    refetchInterval: 5000,
  });
};

export const useCreateInverterCommand = (inverterId: string | undefined) => {
  const queryClient = useQueryClient();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const meterId = selectedPowerPlant?.meterId;

  return useMutation({
    mutationFn: (body: {
      command: InverterCommandType;
      params: InverterCommandParams;
    }) =>
      axiosInstance.post<InverterCommandResponse>(
        INVERTER.COMMANDS(meterId!, inverterId!),
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INVERTER.COMMANDS(meterId!, inverterId!)],
      });
      queryClient.invalidateQueries({
        queryKey: [INVERTER.INVERTERS(meterId!)],
      });
    },
  });
};
